// React
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useState } from "react";

// @mui material components
import { Card, CircularProgress, Fade, Grid, Icon, InputLabel, Modal, Tooltip, Select, MenuItem, Autocomplete, TextField, Divider, Switch, FormControlLabel } from "@mui/material";

// Apollo
import { gql, useMutation, useQuery } from "@apollo/client";

// UI components
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";

// Context
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "60%", xxl: "50%" },
  overflowY: "auto",
  p: 3,
};

export default function ModalAddPresupuesto({ open, handleClose, idEvento, refetch, onCreated }) {
  const { handleSnackbar } = useContext(MessageManager);
  const { confirmar } = useContext(ConfirmarContext);
  const [presupuesto, setPresupuesto] = useState();
  const [step, setStep] = useState(1); // 1 = presupuesto, 2 = productos
  // Estado Step 2 (buffer en memoria)
  const [productoSel, setProductoSel] = useState(null);
  const [cantidad, setCantidad] = useState(1);
  const [precio, setPrecio] = useState(0);
  const [nombreDetalle, setNombreDetalle] = useState("");
  const [search, setSearch] = useState("");
  const [detallesBuffer, setDetallesBuffer] = useState([]); // { idProducto?, nombre, cantidadAsignada, precioUnitario }
  const [savingAll, setSavingAll] = useState(false);
  const [detallesExpanded, setDetallesExpanded] = useState(false);
  const [detallesProdExpanded, setDetallesProdExpanded] = useState(false);
  const [detallesPersExpanded, setDetallesPersExpanded] = useState(false);
  const [incluirPersonal, setIncluirPersonal] = useState(false);
  // Empleados (Step 2, lado derecho cuando incluirPersonal=true)
  const [empleadoSel, setEmpleadoSel] = useState(null);
  const [horasEmpleado, setHorasEmpleado] = useState(0);
  const [nombreDetalleEmpleado, setNombreDetalleEmpleado] = useState("");
  const [searchEmpleado, setSearchEmpleado] = useState("");
  const [itemsServicio, setItemsServicio] = useState("");

  const handleChanges = (e) => {
    setPresupuesto({
      ...presupuesto,
      [e.target.name]: e.target.value,
    });
  };

  const [savePresupuestoConDetalles, { loading }] = useMutation(
    gql`
      mutation savePresupuestoEventoConDetalles($input: PresupuestoEventoInput!, $detalles: [PresupuestoEventoDetalleInput!]!) {
        savePresupuestoEventoConDetalles(input: $input, detalles: $detalles) {
          id
        }
      }
    `
  );

  const [savePresupuesto, { loading: loadingSave }] = useMutation(
    gql`
      mutation savePresupuestoEvento($input: PresupuestoEventoInput!) {
        savePresupuestoEvento(input: $input) {
          id
        }
      }
    `
  );

  // Productos para step 2
  const { data: productosData, loading: loadingProductos } = useQuery(
    gql`
      query getProductos(
        $pagination: PaginationInput
        $order: Order
        $search: String
        $filter: JSON
        $sinIngredientes: Boolean
        $materiaPrimaDeshabilitada: Boolean
      ) {
        productos(
          pagination: $pagination
          order: $order
          search: $search
          filter: $filter
          sinIngredientes: $sinIngredientes
          materiaPrimaDeshabilitada: $materiaPrimaDeshabilitada
        ) {
          id
          nombre
          precioCarta
        }
      }
    `,
    {
      variables: {
        pagination: { page: 1, limit: 50 },
        order: { field: "nombre", order: "ASC" },
        search: search || null,
        filter: { esIngrediente: false },
        sinIngredientes: false,
        materiaPrimaDeshabilitada: false,
      },
      fetchPolicy: "network-only",
      skip: !open || step !== 2,
    }
  );

  const handleSave = () => {
    savePresupuesto({
      variables: {
        input: {
          idEvento,
          fecha: presupuesto?.fecha || undefined,
          vencimiento: presupuesto?.vencimiento || undefined,
          estado: presupuesto?.estado || undefined,
          lugar: presupuesto?.lugar || undefined,
          personal: presupuesto?.personal || false,
          monto: presupuesto?.monto ? parseFloat(presupuesto.monto) : undefined,
          personal: incluirPersonal,
        },
      },
    })
      .then(() => {
        if (typeof refetch === "function") refetch();
        handleSnackbar("Presupuesto creado correctamente", "success");
        handleClose();
      })
      .catch(() => {
        handleSnackbar("Error al crear el presupuesto", "error");
      });
  };

  const handleNextToProductos = () => {
    // Avanzar sin guardar todavía
    setStep(2);
  };

  const handleBackToPresupuesto = () => {
    setStep(1);
    setDetallesExpanded(false);
  };

  const handleCancelar = () => {
    // Cerrar y limpiar todo
    setPresupuesto();
    setProductoSel(null);
    setCantidad(1);
    setPrecio(0);
    setNombreDetalle("");
    setDetallesBuffer([]);
    setStep(1);
    setDetallesExpanded(false);
    handleClose();
  };

  const options = useMemo(() => productosData?.productos || [], [productosData]);
  const totalBuffer = useMemo(
    () => (detallesBuffer || []).reduce((acc, d) => acc + (Number(d?.cantidadAsignada) || 0) * (Number(d?.precioUnitario) || 0), 0),
    [detallesBuffer]
  );
  const productItems = useMemo(
    () =>
      (detallesBuffer || [])
        .map((d, idx) => ({ d, idx }))
        .filter(({ d }) => !d?.idEmpleado)
        .sort(({ d: a }, { d: b }) => {
          const idA = a?.idItem;
          const idB = b?.idItem;

          // Ambos sin idItem → iguales
          if (idA == null && idB == null) return 0;
          // A sin idItem → va después
          if (idA == null) return 1;
          // B sin idItem → va después
          if (idB == null) return -1;
          // Ambos con idItem → orden numérico ascendente
          return idA - idB;
        }),
    [detallesBuffer]
  );

  const personalItems = useMemo(
    () => (detallesBuffer || []).map((d, idx) => ({ d, idx })).filter(({ d }) => !!d?.idEmpleado),
    [detallesBuffer]
  );
  const totalProductos = useMemo(
    () => productItems.reduce((acc, { d }) => acc + (Number(d?.cantidadAsignada) || 0) * (Number(d?.precioUnitario) || 0), 0),
    [productItems]
  );
  const totalHoras = useMemo(
    () => personalItems.reduce((acc, { d }) => acc + (Number(d?.cargaHorariaEmpleado) || 0), 0),
    [personalItems]
  );

  const handleToggleIncluirPersonal = (checked) => {
    if (checked) {
      setIncluirPersonal(true);
      return;
    }
    // Desactivar: confirmar si hay personal acumulado en buffer
    if ((personalItems || []).length > 0) {
      confirmar({
        title: "Quitar personal",
        message: "Hay personal asignado a este presupuesto ¿desea quitarlos?\nEsta acción eliminará todas las asignaciones de personal.",
        icon: "warning",
        func: async () => {
          setDetallesBuffer((prev) => (prev || []).filter((d) => !d?.idEmpleado));
          setEmpleadoSel(null);
          setHorasEmpleado(0);
          setNombreDetalleEmpleado("");
          setDetallesPersExpanded(false);
          setIncluirPersonal(false);
          handleSnackbar("Se quitó el personal asignado", "success");
        },
      });
    } else {
      // No hay personal cargado: sólo desactivar y limpiar UI
      setEmpleadoSel(null);
      setHorasEmpleado(0);
      setNombreDetalleEmpleado("");
      setDetallesPersExpanded(false);
      setIncluirPersonal(false);
    }
  };

  // Empleados query (sólo cuando incluirPersonal y en Step 2)
  const GET_USUARIOS = gql`
    query getUsuarios($search: String, $page: Int, $order: Order) {
      paginationInfo {
        pages
        total
      }
      usuarios(
        search: $search
        pagination: { page: $page, limit: 10 }
        order: $order
        filter: { tipoUser: ["Empleado", "Mozo", "UproDay"] }
      ) {
        id
        nombre
        apellido
        tipoUser
      }
    }
  `;

  const { data: empleadosData, loading: loadingEmpleados } = useQuery(GET_USUARIOS, {
    variables: {
      search: searchEmpleado || null,
      page: 1,
      order: { field: "apellido", order: "ASC" },
    },
    fetchPolicy: "network-only",
    skip: !open || step !== 2 || !incluirPersonal,
  });

  const { data: dataItems, loading: loadingItems } = useQuery(
    gql`
        query getItemsEvento {
          itemsPresupuesto {
            id
            nombre
          }
        }
      `
  );

  const empleadoOptions = useMemo(() => empleadosData?.usuarios || [], [empleadosData]);

  const addDetalleToBuffer = () => {
    const nuevo = {
      idProducto: productoSel?.id || undefined,
      nombre: nombreDetalle || productoSel?.nombre || undefined,
      cantidadAsignada: Number(cantidad) || 1,
      precioUnitario: Number(precio) || productoSel?.precioCarta || 0,
      idItem: itemsServicio || null,
    };
    if (!nuevo.nombre) {
      handleSnackbar("Debe ingresar un nombre o seleccionar un producto", "warning");
      return;
    }
    setDetallesBuffer((prev) => [...prev, nuevo]);
    setProductoSel(null);
    setCantidad(1);
    setPrecio(0);
    setItemsServicio("");
    setNombreDetalle("");
  };

  const addEmpleadoDetalleToBuffer = () => {
    const nombreEmp = nombreDetalleEmpleado || (empleadoSel ? `${empleadoSel.apellido ? empleadoSel.apellido + ", " : ""}${empleadoSel?.nombre || "Empleado"}` : undefined);
    const nuevo = {
      idEmpleado: empleadoSel?.id || undefined,
      cargaHorariaEmpleado: Number(horasEmpleado) || 0,
      nombre: nombreEmp,
    };
    if (!nuevo.idEmpleado) {
      handleSnackbar("Debe seleccionar un empleado", "warning");
      return;
    }
    setDetallesBuffer((prev) => [...prev, nuevo]);
    setEmpleadoSel(null);
    setHorasEmpleado(0);
    setItemsServicio("");
    setNombreDetalleEmpleado("");
  };

  const removeDetalleFromBuffer = (index) => {
    setDetallesBuffer((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGuardarFinal = async () => {
    try {
      setSavingAll(true);
      const hasPersonal = (detallesBuffer || []).some((d) => !!d?.idEmpleado);
      const detalles = (detallesBuffer || []).map((d) => ({
        idProducto: d.idProducto,
        idEmpleado: d.idEmpleado,
        cargaHorariaEmpleado: typeof d.cargaHorariaEmpleado === 'number' ? d.cargaHorariaEmpleado : Number(d.cargaHorariaEmpleado) || 0,
        nombre: d.nombre,
        cantidadAsignada: typeof d.cantidadAsignada === 'number' ? d.cantidadAsignada : Number(d.cantidadAsignada) || 0,
        precioUnitario: typeof d.precioUnitario === 'number' ? d.precioUnitario : Number(d.precioUnitario) || 0,
        idItem: d.idItem || null,
      }));
      await savePresupuestoConDetalles({
        variables: {
          input: {
            idEvento,
            fecha: presupuesto?.fecha || undefined,
            vencimiento: presupuesto?.vencimiento || undefined,
            estado: presupuesto?.estado || undefined,
            lugar: presupuesto?.lugar || undefined,
            personal: hasPersonal,
            monto: presupuesto?.monto ? parseFloat(presupuesto.monto) : undefined,
          },
          detalles,
        },
      });
      if (typeof refetch === "function") await refetch();
      handleSnackbar("Presupuesto y detalles guardados", "success");
      handleCancelar();
    } catch (e) {
      handleSnackbar("Error al guardar presupuesto y detalles", "error");
    } finally {
      setSavingAll(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setPresupuesto();
      setProductoSel(null);
      setCantidad(1);
      setPrecio(0);
      setNombreDetalle("");
      setDetallesBuffer([]);
      setStep(1);
      setSearch("");
      setDetallesExpanded(false);
      setIncluirPersonal(false);
      setEmpleadoSel(null);
      setHorasEmpleado(0);
      setNombreDetalleEmpleado("");
      setSearchEmpleado("");
    }
  }, [open]);

  // controles de estado

  return (
    <Modal aria-labelledby="modal-add-presupuesto" open={open} onClose={handleClose} closeAfterTransition>
      <Fade in={open}>
        <Card sx={{
          ...style,
          width: incluirPersonal ? { xs: "95%", sm: "80%", md: "75%", lg: "70%", xl: "65%" } : style.width,
          maxHeight: step === 1 ? "unset" : style.maxHeight,
          overflowY: step === 1 ? "visible" : "auto",
        }}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftBox>
                <SoftTypography variant="h6">Nuevo presupuesto</SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="space-between">
                <SoftBox>
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>

            {/* Contenedor tipo carrusel */}
            <SoftBox py={2} sx={{ overflow: "hidden" }}>
              <SoftBox
                sx={{
                  display: "flex",
                  width: "200%",
                  transform: step === 1 ? "translateX(0%)" : "translateX(-50%)",
                  transition: "transform 450ms ease",
                }}
              >
                {/* STEP 1: Presupuesto */}
                <SoftBox sx={{ width: "50%", pr: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <InputLabel htmlFor="fecha">
                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                          Fecha
                        </SoftTypography>
                      </InputLabel>
                      <SoftInput
                        label="fecha"
                        placeholder="YYYY-MM-DD"
                        type="date"
                        name="fecha"
                        value={presupuesto?.fecha || ""}
                        onChange={handleChanges}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputLabel htmlFor="vencimiento">
                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                          Vencimiento
                        </SoftTypography>
                      </InputLabel>
                      <SoftInput
                        label="vencimiento"
                        placeholder="YYYY-MM-DD"
                        type="date"
                        name="vencimiento"
                        value={presupuesto?.vencimiento || ""}
                        onChange={handleChanges}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputLabel htmlFor="estado">
                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                          Estado
                        </SoftTypography>
                      </InputLabel>
                      <Select name="estado" value={presupuesto?.estado || ""} onChange={handleChanges}>
                        <MenuItem value="" disabled>
                          Seleccione estado
                        </MenuItem>
                        <MenuItem value="Pendiente">Pendiente</MenuItem>
                        <MenuItem value="Aprobado">Aprobado</MenuItem>
                        <MenuItem value="Rechazado">Rechazado</MenuItem>
                      </Select>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputLabel htmlFor="lugar">
                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                          Lugar
                        </SoftTypography>
                      </InputLabel>
                      <SoftInput
                        label="lugar"
                        placeholder="Ej: Salón principal"
                        type="text"
                        name="lugar"
                        value={presupuesto?.lugar || ""}
                        onChange={handleChanges}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputLabel htmlFor="monto">
                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                          Monto estimado
                        </SoftTypography>
                      </InputLabel>
                      <SoftInput
                        label="monto"
                        placeholder="$"
                        type="number"
                        name="monto"
                        value={presupuesto?.monto || ""}
                        onChange={handleChanges}
                      />
                    </Grid>
                    {/* <Grid item xs={12} sm={6} display="flex" alignItems="center">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(presupuesto?.personal)}
                            onChange={(e) => setPresupuesto({ ...presupuesto, personal: e.target.checked })}
                          />
                        }
                        label="Incluye personal"
                      />
                    </Grid> */}
                  </Grid>
                </SoftBox>

                {/* STEP 2: Productos (buffer) */}
                <SoftBox sx={{ width: "50%", pl: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between">
                      <InputLabel>
                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                          Contenido del detalle
                        </SoftTypography>
                      </InputLabel>
                      <FormControlLabel
                        control={<Switch checked={incluirPersonal} onChange={(e) => handleToggleIncluirPersonal(e.target.checked)} />}
                        label="Incluir personal"
                      />
                    </Grid>

                    {/* Columna izquierda: Productos */}
                    <Grid item xs={12} sm={incluirPersonal ? 6 : 12}>
                      <SoftBox sx={{ p: 2, border: "1px solid", borderColor: "grey.200", borderRadius: 2, backgroundColor: "grey.50" }} pb={1}>
                        <SoftTypography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Productos</SoftTypography>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12}>
                            <InputLabel>
                              <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Producto (opcional)
                              </SoftTypography>
                            </InputLabel>
                            <Autocomplete
                              value={productoSel}
                              options={options}
                              getOptionLabel={(option) => option?.nombre || ""}
                              loading={loadingProductos}
                              noOptionsText="No hay resultados"
                              onInputChange={(e, value) => setSearch(value)}
                              onChange={(e, newValue) => {
                                setProductoSel(newValue);
                                setPrecio(newValue?.precioCarta || 0);
                                if (newValue?.nombre) setNombreDetalle(newValue.nombre);
                              }}
                              renderInput={(params) => <TextField {...params} placeholder="Buscar producto..." />}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <InputLabel>
                              <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Nombre del detalle
                              </SoftTypography>
                            </InputLabel>
                            <SoftInput
                              placeholder={productoSel?.nombre || "Ingrese el nombre del detalle"}
                              type="text"
                              name="nombreDetalle"
                              value={nombreDetalle}
                              onChange={(e) => setNombreDetalle(e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={incluirPersonal ? 12 : 3}>
                            <InputLabel>
                              <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Servicio
                              </SoftTypography>
                            </InputLabel>
                            <Select
                              value={itemsServicio}
                              onChange={(e) => setItemsServicio(e.target.value)}
                              displayEmpty
                              inputProps={{ "aria-label": "Mes" }}
                            >
                              <MenuItem value="">
                                <em>Ninguno</em>
                              </MenuItem>
                              {(dataItems?.itemsPresupuesto || []).map(({ nombre, id }) => (
                                <MenuItem key={`${id}-${nombre}`} value={id}>
                                  {nombre}
                                </MenuItem>
                              ))}
                            </Select>
                          </Grid>
                          <Grid item xs={12} sm={incluirPersonal ? 6 : 3}>
                            <InputLabel>
                              <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Cantidad
                              </SoftTypography>
                            </InputLabel>
                            <SoftInput
                              placeholder="1"
                              type="number"
                              name="cantidad"
                              value={cantidad}
                              onChange={(e) => setCantidad(e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={incluirPersonal ? 6 : 3}>
                            <InputLabel>
                              <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Precio unitario
                              </SoftTypography>
                            </InputLabel>
                            <SoftInput
                              placeholder="0.00"
                              type="number"
                              name="precio"
                              value={precio}
                              onChange={(e) => setPrecio(e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} display="flex" justifyContent={incluirPersonal ? "flex-start" : "flex-end"}>
                            <SoftButton color="info" onClick={addDetalleToBuffer}>
                              <Icon sx={{ mr: 1 }}>add</Icon>
                              Agregar detalle
                            </SoftButton>
                          </Grid>
                        </Grid>
                      </SoftBox>
                    </Grid>

                    {/* Columna derecha: Personal */}
                    {incluirPersonal && (
                      <Grid item xs={12} sm={6}>
                        <SoftBox sx={{ p: 2, border: "1px solid", borderColor: "grey.200", borderRadius: 2, backgroundColor: "grey.50" }}>
                          <SoftTypography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Personal</SoftTypography>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <InputLabel>
                                <SoftTypography component="label" variant="caption" fontWeight="bold">
                                  Empleado
                                </SoftTypography>
                              </InputLabel>
                              <Autocomplete
                                value={empleadoSel}
                                options={empleadoOptions}
                                getOptionLabel={(option) => `${option?.apellido ? option.apellido + ', ' : ''}${option?.nombre || ''}`}
                                loading={loadingEmpleados}
                                noOptionsText="No hay resultados"
                                onInputChange={(e, value) => setSearchEmpleado(value)}
                                onChange={(e, newValue) => {
                                  setEmpleadoSel(newValue);
                                  if (newValue?.nombre || newValue?.apellido) {
                                    setNombreDetalleEmpleado(`${newValue?.apellido ? newValue.apellido + ', ' : ''}${newValue?.nombre || ''}`);
                                  }
                                }}
                                renderInput={(params) => <TextField {...params} placeholder="Buscar empleado..." />}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <InputLabel>
                                <SoftTypography component="label" variant="caption" fontWeight="bold">
                                  Nombre del detalle (opcional)
                                </SoftTypography>
                              </InputLabel>
                              <SoftInput
                                placeholder={empleadoSel ? `${empleadoSel?.apellido ? empleadoSel.apellido + ', ' : ''}${empleadoSel?.nombre || ''}` : "Ingrese el nombre del detalle"}
                                type="text"
                                name="nombreDetalleEmpleado"
                                value={nombreDetalleEmpleado}
                                onChange={(e) => setNombreDetalleEmpleado(e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <InputLabel>
                                <SoftTypography component="label" variant="caption" fontWeight="bold">
                                  Carga horaria (hs)
                                </SoftTypography>
                              </InputLabel>
                              <SoftInput
                                placeholder="0"
                                type="number"
                                name="horasEmpleado"
                                value={horasEmpleado}
                                onChange={(e) => setHorasEmpleado(e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12} display="flex" justifyContent="flex-end">
                              <SoftButton color="primary" variant="outlined" onClick={addEmpleadoDetalleToBuffer}>
                                <Icon sx={{ mr: 1 }}>person_add</Icon>
                                Agregar personal
                              </SoftButton>
                            </Grid>
                          </Grid>
                        </SoftBox>
                      </Grid>
                    )}
                    {/* Listas independientes */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      {/* Productos agregados */}
                      <SoftBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          borderBottom: "2px solid", borderColor: "grey.200", pb: 2, mb: 2, cursor: "pointer",
                          "&:hover": { backgroundColor: "grey.50" }, transition: "background-color 0.2s ease"
                        }}
                        onClick={() => setDetallesProdExpanded(!detallesProdExpanded)}
                      >
                        <SoftBox display="flex" alignItems="center">
                          <Icon sx={{ fontSize: "1.5rem", mr: 1, color: "info.main" }}>shopping_cart</Icon>
                          <SoftTypography variant="h6" fontWeight="bold">
                            Productos agregados ({productItems.length})
                          </SoftTypography>
                          <Icon sx={{ ml: 1, color: "text.secondary", transform: detallesProdExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s ease" }}>expand_more</Icon>
                        </SoftBox>
                        <SoftBox sx={{ background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)", color: "white", px: 2, py: 1, borderRadius: 2, boxShadow: "0 2px 8px rgba(76, 175, 80, 0.3)" }}>
                          <SoftTypography variant="subtitle2" fontWeight="bold">Total: $ {Number(totalProductos).toLocaleString()}</SoftTypography>
                        </SoftBox>
                      </SoftBox>
                      {detallesProdExpanded && (
                        <SoftBox sx={{
                          maxHeight: "300px", overflowY: "auto", animation: "fadeIn 0.3s ease-in-out",
                          "@keyframes fadeIn": { "0%": { opacity: 0, transform: "translateY(-10px)" }, "100%": { opacity: 1, transform: "translateY(0)" } }
                        }}>
                          {productItems.length === 0 ? (
                            <SoftTypography variant="caption" color="text">No hay productos agregados</SoftTypography>
                          ) : (
                            <Grid container spacing={1}>
                              {productItems.map(({ d, idx }) => (
                                <Grid key={`prod-${idx}`} item xs={12}>
                                  <SoftBox display="flex" alignItems="center" justifyContent="space-between" sx={{ p: 1, border: "1px solid", borderColor: "grey.200", borderRadius: 1, "&:hover": { borderColor: "info.main", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }, transition: "all 0.2s ease" }}>
                                    <SoftBox>
                                      <SoftTypography variant="button" fontWeight="medium">{d.nombre}</SoftTypography>
                                      <SoftBox>
                                        <SoftTypography variant="caption" color="text" sx={{ mr: 2 }}> <strong>Cant:</strong> {d.cantidadAsignada}</SoftTypography>
                                        <SoftTypography variant="caption" color="text"><strong>$</strong> {Number(d.precioUnitario).toLocaleString()}</SoftTypography>
                                        {d.idItem && (<SoftTypography variant="caption" color="text" ml={1.5}><strong>{((dataItems?.itemsPresupuesto || []).find(item => item.id == d?.idItem)?.nombre ?? "")}</strong></SoftTypography>)}
                                      </SoftBox>
                                    </SoftBox>
                                    <SoftBox display="flex" alignItems="center">
                                      <SoftTypography variant="button" fontWeight="bold" color="success" sx={{ mr: 2 }}>
                                        $ {(Number(d.cantidadAsignada) * Number(d.precioUnitario)).toLocaleString()}
                                      </SoftTypography>
                                      <Tooltip title="Eliminar" placement="top"><Icon sx={{ cursor: "pointer", color: "error.main", "&:hover": { color: "error.dark", transform: "scale(1.1)" }, transition: "all 0.2s ease" }} onClick={() => removeDetalleFromBuffer(idx)}>delete</Icon></Tooltip>
                                    </SoftBox>
                                  </SoftBox>
                                </Grid>
                              ))}
                            </Grid>
                          )}
                        </SoftBox>
                      )}

                      {/* Personal asignado */}
                      <SoftBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          borderBottom: "2px solid", borderColor: "grey.200", pb: 2, mb: 2, mt: 3, cursor: "pointer",
                          "&:hover": { backgroundColor: "grey.50" }, transition: "background-color 0.2s ease"
                        }}
                        onClick={() => setDetallesPersExpanded(!detallesPersExpanded)}
                      >
                        <SoftBox display="flex" alignItems="center">
                          <Icon sx={{ fontSize: "1.5rem", mr: 1, color: "info.main" }}>person</Icon>
                          <SoftTypography variant="h6" fontWeight="bold">
                            Personal asignado ({personalItems.length})
                          </SoftTypography>
                          <Icon sx={{ ml: 1, color: "text.secondary", transform: detallesPersExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s ease" }}>expand_more</Icon>
                        </SoftBox>
                        <SoftBox sx={{ background: "linear-gradient(135deg, #2196F3 0%, #1E88E5 100%)", color: "white", px: 2, py: 1, borderRadius: 2, boxShadow: "0 2px 8px rgba(33, 150, 243, 0.3)" }}>
                          <SoftTypography variant="subtitle2" fontWeight="bold">Total horas: {Number(totalHoras).toLocaleString()}</SoftTypography>
                        </SoftBox>
                      </SoftBox>
                      {detallesPersExpanded && (
                        <SoftBox sx={{
                          maxHeight: "300px", overflowY: "auto", animation: "fadeIn 0.3s ease-in-out",
                          "@keyframes fadeIn": { "0%": { opacity: 0, transform: "translateY(-10px)" }, "100%": { opacity: 1, transform: "translateY(0)" } }
                        }}>
                          {personalItems.length === 0 ? (
                            <SoftTypography variant="caption" color="text">No hay personal asignado</SoftTypography>
                          ) : (
                            <Grid container spacing={1}>
                              {personalItems.map(({ d, idx }) => (
                                <Grid key={`pers-${idx}`} item xs={12}>
                                  <SoftBox display="flex" alignItems="center" justifyContent="space-between" sx={{ p: 1, border: "1px solid", borderColor: "grey.200", borderRadius: 1, "&:hover": { borderColor: "info.main", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }, transition: "all 0.2s ease" }}>
                                    <SoftBox>
                                      <SoftTypography variant="button" fontWeight="medium">{d.nombre || `Empleado #${d.idEmpleado}`}</SoftTypography>
                                      <SoftBox>
                                        <SoftTypography variant="caption" color="text">Horas: {Number(d.cargaHorariaEmpleado)}</SoftTypography>
                                      </SoftBox>
                                    </SoftBox>
                                    <SoftBox display="flex" alignItems="center">
                                      <Tooltip title="Eliminar" placement="top"><Icon sx={{ cursor: "pointer", color: "error.main", "&:hover": { color: "error.dark", transform: "scale(1.1)" }, transition: "all 0.2s ease" }} onClick={() => removeDetalleFromBuffer(idx)}>delete</Icon></Tooltip>
                                    </SoftBox>
                                  </SoftBox>
                                </Grid>
                              ))}
                            </Grid>
                          )}
                        </SoftBox>
                      )}
                    </Grid>
                  </Grid>
                </SoftBox>
              </SoftBox>
            </SoftBox>

            {/* Botonera inferior según step */}
            {step === 1 ? (
              <SoftBox display={{ xs: "block", sm: "flex" }} justifyContent="space-between" alignItems="center" mt={2}>
                <SoftBox>
                  <SoftButton color="secondary" variant="outlined" onClick={handleCancelar}>
                    <Icon sx={{ mr: 1 }}>cancel</Icon>
                    Cancelar
                  </SoftButton>
                </SoftBox>
                <SoftBox display="flex" gap={1} mt={{ xs: 2, sm: 0 }}>
                  <SoftButton color="uproGreen" disabled={loading || !idEvento} onClick={handleSave}>
                    {loading || loadingSave ? <CircularProgress size={15} color="inherit" /> : <Icon sx={{ mr: 1 }}>save</Icon>}
                    Guardar y salir
                  </SoftButton>
                  <SoftButton color="info" disabled={loading || loadingSave} onClick={handleNextToProductos}>
                    Asignar productos
                    <Icon sx={{ ml: 1 }}>arrow_forward</Icon>
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            ) : (
              <SoftBox display={{ xs: "block", sm: "flex" }} justifyContent="space-between" alignItems="center" mt={2}>
                <SoftBox>
                  <SoftButton color="secondary" variant="outlined" onClick={handleCancelar}>
                    <Icon sx={{ mr: 1 }}>cancel</Icon>
                    Cancelar
                  </SoftButton>
                </SoftBox>
                <SoftBox display="flex" gap={1} mt={{ xs: 2, sm: 0 }}>
                  <SoftButton color="primary" variant="outlined" onClick={handleBackToPresupuesto}>
                    <Icon sx={{ mr: 1 }}>arrow_back</Icon>
                    Volver a presupuesto
                  </SoftButton>
                  <SoftButton color="uproGreen" disabled={savingAll || !idEvento} onClick={handleGuardarFinal}>
                    {savingAll ? <CircularProgress size={15} color="inherit" /> : <Icon sx={{ mr: 1 }}>save</Icon>}
                    Guardar
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            )}
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalAddPresupuesto.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  idEvento: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  refetch: PropTypes.func,
  onCreated: PropTypes.func,
};
